/**
 * 인증 방식을 apiFetch 에서 떼어낸 자리(seam).
 *
 * 웹은 세션 쿠키로 인증한다 — 브라우저가 쿠키를 자동으로 실어 보내므로 CSRF 토큰을 헤더로 되돌려준다.
 * 추후 네이티브 앱(RN)은 쿠키 대신 `Authorization: Bearer` 로 인증하고, 401 이면 access 토큰을
 * refresh 로 재발급한다. 그때 앱은 자기만의 AuthTransport 를 만들어 {@link setAuthTransport} 로
 * 갈아 끼우면 되고, `apiFetch` 본문·컴포넌트는 손대지 않는다.
 *
 * 지금 구현은 쿠키 하나뿐이다. 이 파일의 존재 이유는 "그 하나가 유일한 방식이라고 apiFetch 가
 * 가정하지 않게" 하는 것뿐이다. 설계 배경은 docs/04_2026-07-28_cross-platform-mobile-design.md.
 */

export type ApiRequestContext = {
  /** 상태를 바꾸는 요청(POST/PUT/PATCH/DELETE)인가. 쿠키 방식의 CSRF 판단에 쓴다. */
  unsafe: boolean;
};

export interface AuthTransport {
  /**
   * fetch 의 credentials. 쿠키 방식은 `'include'`(세션 쿠키 전송),
   * 토큰 방식은 `'omit'`(쿠키를 아예 쓰지 않음)이 된다.
   */
  readonly credentials: RequestCredentials;

  /**
   * 이 요청에 붙일 인증 헤더. 쿠키 방식은 상태 변경 요청에 `X-XSRF-TOKEN` 을,
   * 토큰 방식은 `Authorization: Bearer …` 를 돌려줄 자리다.
   */
  authHeaders(ctx: ApiRequestContext): Record<string, string>;

  /**
   * 401 을 받았을 때 한 번 호출된다. 토큰 방식이 refresh 로 access 토큰을 재발급하고
   * `true` 를 돌려주면 apiFetch 가 원 요청을 **한 번** 재시도한다.
   * 쿠키 방식은 브라우저가 세션 쿠키를 알아서 관리하므로 여기서 되살릴 게 없어 정의하지 않는다.
   */
  onUnauthorized?(): Promise<boolean>;

  /**
   * 상태 변경 요청(POST/PUT/PATCH/DELETE)을 보내기 직전에 호출된다. 쿠키 방식은 여기서
   * `XSRF-TOKEN` 쿠키가 없으면 안전한 GET 으로 먼저 발급받는다 — 이것이 없으면 첫 화면 진입
   * 직후나 쿠키가 지워진 상태에서 CSRF 헤더가 비어 나가 모든 쓰기가 403 이 된다.
   */
  ensureReady?(baseUrl: string): Promise<void>;
}

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';

/**
 * 쿠키 한 칸을 읽는다. <b>이름이 같은 쿠키가 여러 개일 수 있다</b> — 그때는 마지막 것을 쓴다.
 *
 * <h3>왜 마지막인가</h3>
 * {@code document.cookie} 는 이름이 겹쳐도 걸러 주지 않고 전부 늘어놓는다. 순서는 RFC 6265 가
 * 정한다 — <b>path 가 긴 것 먼저, 같으면 먼저 만들어진 것 먼저</b>. 그래서 겹쳤을 때 앞에 오는 것은
 * 대개 <b>옛 배포가 남긴 쪽</b>(더 좁은 path 이거나 더 오래된 것)이고, 서버가 방금 심은 정본은 뒤에 있다.
 *
 * 예전에는 첫 매치를 집었다. 그러면 낡은 CSRF 토큰이 헤더로 되돌아가 <b>로그인부터 모든 쓰기가
 * 403</b> 이 되는데, 서버 쪽 CSRF 403 은 어느 로그에도 남지 않아 화면에도 로그에도 원인이 안 보인다.
 * 게다가 쿠키는 브라우저 프로필에 붙어 있어 <b>"이 사람 Edge 만 안 되고 Chrome 은 된다"</b> 같은,
 * 브라우저 탓으로 보이는 모습으로 나타난다 — 실제로는 그 프로필에 옛 쿠키가 남아 있는 것뿐이다.
 *
 * 값이 빈 칸인 쿠키는 건너뛴다. 만료시키려고 심는 쿠키({@code NAME=; expires=…})가 정본을 가리지
 * 않게 하기 위해서다.
 */
function readCookie(name: string): string | null {
  const pattern = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`, 'g');
  let last: string | null = null;
  for (const match of document.cookie.matchAll(pattern)) {
    if (match[1]) last = match[1];
  }
  return last === null ? null : decodeURIComponent(last);
}

/**
 * 웹 기본 방식: 세션 쿠키 + CSRF.
 *
 * 쿠키 인증은 브라우저가 요청을 자동으로 실어 보내므로 CSRF 방어가 필수다. 백엔드가 내려준
 * `XSRF-TOKEN` 쿠키를 읽어 `X-XSRF-TOKEN` 헤더로 되돌려준다(상태 변경 요청에만).
 */
export const cookieAuthTransport: AuthTransport = {
  credentials: 'include',
  authHeaders({ unsafe }) {
    const headers: Record<string, string> = {};
    if (!unsafe) return headers;
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
    return headers;
  },
  async ensureReady(baseUrl) {
    if (readCookie(CSRF_COOKIE)) return;
    // 토큰 쿠키가 없다 — /session 은 공개 GET 이라 로그인 여부와 무관하게 XSRF-TOKEN 을 내려준다.
    // 실패는 삼킨다: 여기서 못 받아도 이어지는 요청이 그대로 403 을 돌려주고 그 경로에서 다뤄진다.
    try {
      await fetch(`${baseUrl}/v1/auth/session`, { credentials: 'include', headers: { Accept: 'application/json' } });
    } catch {
      /* 네트워크 실패는 원 요청이 판단하게 둔다 */
    }
  },
};

let current: AuthTransport = cookieAuthTransport;

export function getAuthTransport(): AuthTransport {
  return current;
}

/**
 * 인증 방식을 갈아 끼운다. 네이티브 앱이 시작 시 Bearer 방식을 넣는 자리다.
 * 웹은 기본값(cookieAuthTransport)을 그대로 둔다.
 */
export function setAuthTransport(transport: AuthTransport): void {
  current = transport;
}
